import { Text } from '@mantine/core';
import {
  IconArrowBackUp,
  IconArrowForwardUp,
  IconCheck,
  IconCircleDashed,
  IconEqual,
  IconMathEqualGreater,
  IconMathEqualLower,
  IconMathGreater,
  IconMathLower,
  IconX,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import type { ReactNode } from 'react';
import {
  flat,
  isDefined,
  isEmpty,
  isNullish,
  isNumber,
  map,
  mapValues,
  pipe,
  prop,
  values,
} from 'remeda';
import { assert } from 'shared/utils';
import z from 'zod/v4';

import { ICON_PROPS } from './condition-fields-constants';
import type { AutomationFieldType } from './field-types';

//* This is how you add a property to operators
type _WizardOperator = {
  value: string;
  icon: ReactNode;
  isOperandValid: (operand: string | number | null) => boolean;
} & (
  | { hasOperand: true; defaultOperandValue: string | number }
  | { hasOperand: false; defaultOperandValue: null }
);

//* This is how you add or modifiy operators
export const OPERATOR_DEFINITIONS = {
  string: [
    {
      value: 'is_same_string',
      icon: <IconEqual {...ICON_PROPS} />,
      hasOperand: true,
      defaultOperandValue: '',
      isOperandValid: operand => {
        if (isNullish(operand) || isNumber(operand)) return false;
        return !isEmpty(operand.trim());
      },
    },
    {
      value: 'contains',
      icon: <Text ml={4}>∋</Text>,
      hasOperand: true,
      defaultOperandValue: '',
      isOperandValid: operand => {
        if (isNullish(operand) || isNumber(operand)) return false;
        return !isEmpty(operand.trim());
      },
    },
    {
      value: 'is_empty_string',
      icon: <IconCircleDashed {...ICON_PROPS} />,
      hasOperand: false,
      defaultOperandValue: null,
      isOperandValid: () => true,
    },
  ],
  select: [
    {
      value: 'is_same_string',
      icon: <IconEqual {...ICON_PROPS} />,
      hasOperand: true,
      defaultOperandValue: '',
      isOperandValid: operand => {
        if (isNullish(operand) || isNumber(operand)) return false;
        return !isEmpty(operand.trim());
      },
    },
  ],
  boolean: [
    {
      value: 'is_true',
      icon: <IconCheck {...ICON_PROPS} />,
      hasOperand: false,
      defaultOperandValue: null,
      isOperandValid: () => true,
    },
    {
      value: 'is_false',
      icon: <IconX {...ICON_PROPS} />,
      hasOperand: false,
      defaultOperandValue: null,
      isOperandValid: () => true,
    },
  ],
  number: [
    {
      value: 'is_empty',
      icon: <IconCircleDashed {...ICON_PROPS} />,
      hasOperand: false,
      defaultOperandValue: null,
      isOperandValid: () => true,
    },
    {
      value: 'is_equal',
      icon: <IconEqual {...ICON_PROPS} />,
      hasOperand: true,
      defaultOperandValue: '',
      isOperandValid: operand => !isNullish(operand),
    },
    {
      value: 'is_greater_than',
      icon: <IconMathGreater {...ICON_PROPS} />,
      hasOperand: true,
      defaultOperandValue: '',
      isOperandValid: operand => !isNullish(operand),
    },
    {
      value: 'is_greater_than_or_equal',
      icon: <IconMathEqualGreater {...ICON_PROPS} />,
      hasOperand: true,
      defaultOperandValue: '',
      isOperandValid: operand => !isNullish(operand),
    },
    {
      value: 'is_less_than',
      icon: <IconMathLower {...ICON_PROPS} />,
      hasOperand: true,
      defaultOperandValue: '',
      isOperandValid: operand => !isNullish(operand),
    },
    {
      value: 'is_less_than_or_equal',
      icon: <IconMathEqualLower {...ICON_PROPS} />,
      hasOperand: true,
      defaultOperandValue: '',
      isOperandValid: operand => !isNullish(operand),
    },
  ],
  date: [
    {
      value: 'is_empty_date',
      icon: <IconCircleDashed {...ICON_PROPS} />,
      hasOperand: false,
      defaultOperandValue: null,
      isOperandValid: () => true,
    },
    {
      value: 'is_before',
      icon: <IconArrowBackUp {...ICON_PROPS} />,
      hasOperand: true,
      defaultOperandValue: dayjs().toISOString(),
      isOperandValid: operand => !isNullish(operand) && dayjs(operand).isValid(),
    },
    {
      value: 'is_after',
      icon: <IconArrowForwardUp {...ICON_PROPS} />,
      hasOperand: true,
      defaultOperandValue: dayjs().toISOString(),
      isOperandValid: operand => !isNullish(operand) && dayjs(operand).isValid(),
    },
    {
      value: 'is_same_day',
      icon: <IconEqual {...ICON_PROPS} />,
      hasOperand: true,
      defaultOperandValue: dayjs().toISOString(),
      isOperandValid: operand => !isNullish(operand) && dayjs(operand).isValid(),
    },
  ],
  array: [
    {
      value: 'array_includes',
      icon: <IconEqual {...ICON_PROPS} />,
      hasOperand: true,
      defaultOperandValue: '',
      isOperandValid: operand => {
        if (isNullish(operand) || isNumber(operand)) return false;
        return !isEmpty(operand.trim());
      },
    },
    {
      value: 'array_contains',
      icon: <Text ml={4}>∋</Text>,
      hasOperand: true,
      defaultOperandValue: '',
      isOperandValid: operand => {
        if (isNullish(operand) || isNumber(operand)) return false;
        return !isEmpty(operand.trim());
      },
    },
    {
      value: 'is_empty_array',
      icon: <IconCircleDashed {...ICON_PROPS} />,
      hasOperand: false,
      defaultOperandValue: null,
      isOperandValid: () => true,
    },
  ],
  id: [
    {
      value: 'is_same_id',
      icon: <IconEqual {...ICON_PROPS} />,
      hasOperand: true,
      defaultOperandValue: '',
      isOperandValid: operand => !isNullish(operand) && !isEmpty(String(operand).trim()),
    },
  ],
} as const satisfies Record<AutomationFieldType, Array<_WizardOperator>>;

// #region Internals
const AUTOMATION_OPERATORS = pipe(OPERATOR_DEFINITIONS, values(), flat(), map(prop('value')));

export type AutomationOperator = (typeof AUTOMATION_OPERATORS)[number];

export const AutomationOperatorSchema = z.enum([...AUTOMATION_OPERATORS]);

const FLATTENED_OPERATORS = pipe(OPERATOR_DEFINITIONS, values(), flat());

export function getOperatorDefinition(operatorName: AutomationOperator) {
  const operator = FLATTENED_OPERATORS.find(op => op.value === operatorName);
  assert(isDefined(operator));
  return operator;
}

export function useOperators(): Record<
  AutomationFieldType,
  Array<(typeof OPERATOR_DEFINITIONS)[AutomationFieldType][number] & { label: string }>
> {
  const { t } = useTranslation();

  return pipe(
    OPERATOR_DEFINITIONS,
    mapValues(operators =>
      operators.map(op => ({
        ...op,
        label: t(
          (i18nKeys.AUTOMATION.OPERATORS as any)[op.value.toUpperCase().replaceAll('_', '')],
        ),
      })),
    ),
  );
}
// #endregion
